import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { ModeEditOutlineOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Button from "components/Button";
import Dialog, { DialogContent, DialogTitle } from "components/Dialog";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";

import { companyPackageSnapshotRename } from "entities/Package/sdk";

const SnapshotRenameDialog = ({
  snapshot,
  open,
  onClose,
  onUpdate,
}: {
  snapshot: { id: number; name: string } | null;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}) => {
  const [snapshotName, setSnapshotName] = useState<string>("");

  useEffect(() => {
    if (!_.isNil(snapshot)) {
      setSnapshotName(snapshot.name);
    }
  }, [snapshot]);

  const onSnapshotNameChange = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setSnapshotName(event.target.value),
    [setSnapshotName]
  );

  const params = useParams<{
    dealId: string;
    packageId: string;
  }>();

  const dealId = useMemo(() => Number(params.dealId), [params.dealId]);
  const companyPackageId = useMemo(
    () => Number(params.packageId),
    [params.packageId]
  );

  const onSnapshotRenameSubmit = useCallback(() => {
    if (!_.isNil(snapshot) && !_.isNil(snapshotName)) {
      companyPackageSnapshotRename({
        dealId,
        companyPackageId,
        snapshotId: snapshot.id,
        name: snapshotName,
      })
        .then(() => {
          onUpdate();
          toast.successMessage("The snapshot has been successfully renamed!");
        })
        .catch(handleInvalidRequest)
        .finally(onClose);
    }
  }, [companyPackageId, dealId, snapshot, snapshotName, onClose, onUpdate]);

  const isNameInvalid = useMemo(
    () => _.isNil(snapshotName) || _.isEmpty(snapshotName),
    [snapshotName]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          gap={1}
          padding={2}
          sx={{ background: colors.blue10, borderRadius: 2 }}
        >
          <ModeEditOutlineOutlinedIcon
            sx={{
              width: 24,
              height: 24,
              cursor: "pointer",
              alignSelf: "center",
            }}
          />
          <Text variant="h2">Edit snapshot name</Text>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", marginTop: 0.5 }}
      >
        <Stack marginBottom={0.5}>
          <Text variant="text3" fontWeight={500}>
            Snapshot name
          </Text>
          <Text variant="text4" color={colors.gray40}>
            Re-name this saved snapshot.
          </Text>
        </Stack>
        <Stack gap={4}>
          <TextField
            error={isNameInvalid}
            fullWidth
            slotProps={{
              formHelperText: { sx: { margin: "inherit" } },
            }}
            helperText={
              isNameInvalid && (
                <Text
                  variant="text4"
                  sx={{ backgroundColor: colors.white }}
                  padding={theme.spacing(0.5, 1.5)}
                >
                  Snapshot name cannot be empty!
                </Text>
              )
            }
            type="text"
            value={snapshotName}
            onChange={onSnapshotNameChange}
            placeholder="Snapshot name"
            variant="outlined"
            sx={{ backgroundColor: colors.blue10 }}
          />
          <Stack direction="row" gap={1} alignSelf="flex-end">
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onSnapshotRenameSubmit} disabled={isNameInvalid}>
              Save changes
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SnapshotRenameDialog;
